import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { SimilarItemPreviewResponse } from '../api/classificationSuggestions';
import {
  classificationSourceLabel,
  confidenceBandLabel,
  formatConfidenceDistribution,
} from '../utils/confidenceBandDisplay';

type SimilarItemSuggestionPreviewProps = {
  loading: boolean;
  preview: SimilarItemPreviewResponse | null;
  error: string | null;
  notFound: boolean;
  approving: boolean;
  approveError: string | null;
  rejecting: boolean;
  ignoring: boolean;
  decisionError: string | null;
  onClose: () => void;
  onRetry: () => void;
  onApprove: () => void;
  onReject: () => void;
  onIgnore: () => void;
};

const SimilarItemSuggestionPreview: React.FC<SimilarItemSuggestionPreviewProps> = ({
  loading,
  preview,
  error,
  notFound,
  approving,
  approveError,
  rejecting,
  ignoring,
  decisionError,
  onClose,
  onRetry,
  onApprove,
  onReject,
  onIgnore,
}) => {
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);

  if (loading) {
    return (
      <Box
        className="flex items-center gap-3 py-6"
        data-testid="SIMILAR_ITEM_PREVIEW_LOADING"
        aria-live="polite"
      >
        <CircularProgress size={24} aria-label="Loading similar-item preview" />
        <Typography variant="body2" color="text.secondary">
          Loading preview…
        </Typography>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Alert
        severity="info"
        data-testid="SIMILAR_ITEM_PREVIEW_NOT_FOUND"
        action={
          <Button color="inherit" size="small" onClick={onClose}>
            Close
          </Button>
        }
      >
        This suggestion is no longer available.
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        data-testid="SIMILAR_ITEM_PREVIEW_ERROR"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!preview) {
    return null;
  }

  const distributionText = formatConfidenceDistribution(preview.confidence_band_distribution);
  const isApplying = preview.status === 'applying';
  const canApplyChain = preview.status === 'pending_review' || preview.status === 'approved';
  const canDecide = preview.status === 'pending_review';
  const applyActionsDisabled = !canApplyChain || approving || rejecting || ignoring || isApplying;
  const decisionActionsDisabled = !canDecide || approving || rejecting || ignoring || isApplying;

  const handleConfirmApprove = () => {
    setApproveConfirmOpen(false);
    onApprove();
  };

  const handleConfirmReject = () => {
    setRejectConfirmOpen(false);
    onReject();
  };

  return (
    <Box
      data-testid="SIMILAR_ITEM_PREVIEW"
      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
      aria-live="polite"
    >
      <Box className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <Box>
          <Typography variant="h6" component="h3">
            Similar items
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {preview.prior_domain} → {preview.target_domain}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {preview.affected_count} {preview.affected_count === 1 ? 'item' : 'items'} may match
          </Typography>
        </Box>
        <Button size="small" onClick={onClose} data-testid="SIMILAR_ITEM_PREVIEW_CLOSE">
          Close preview
        </Button>
      </Box>

      {preview.similarity_summary ? (
        <Box className="mb-4 space-y-1">
          <Typography variant="subtitle2" component="h4">
            Why these items
          </Typography>
          <Typography variant="body2">{preview.similarity_summary}</Typography>
        </Box>
      ) : null}

      <Box className="mb-4 space-y-2">
        <Typography variant="subtitle2" component="h4">
          Confidence
        </Typography>
        {distributionText ? (
          <Typography variant="body2" color="text.secondary">
            {distributionText}
          </Typography>
        ) : null}
      </Box>

      <Box className="mb-4 space-y-1">
        <Typography variant="subtitle2" component="h4">
          Manual corrections
        </Typography>
        <Typography variant="body2">{preview.override_impact.statement}</Typography>
      </Box>

      <Box className="mb-4 space-y-1">
        <Typography variant="subtitle2" component="h4">
          Undo posture
        </Typography>
        <Typography variant="body2">{preview.undo_posture.statement}</Typography>
      </Box>

      <Box className="mb-4">
        <Typography variant="subtitle2" component="h4" className="mb-2">
          Example items
        </Typography>
        {preview.example_items.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="SIMILAR_ITEM_PREVIEW_NO_EXAMPLES"
          >
            No example items are available for this suggestion right now.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label="Similar-item example items">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Similarity</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Evidence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.example_items.map((example) => (
                  <TableRow key={example.work_item_id}>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/app/workbench?focusId=${encodeURIComponent(example.work_item_id)}`}
                        underline="hover"
                      >
                        {example.title}
                      </Link>
                    </TableCell>
                    <TableCell>{example.similarity_reason}</TableCell>
                    <TableCell>{confidenceBandLabel(example.confidence_band)}</TableCell>
                    <TableCell>{classificationSourceLabel(example.classification_source)}</TableCell>
                    <TableCell>{example.evidence_summary || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {isApplying ? (
        <Alert severity="info" className="mb-3" data-testid="SIMILAR_ITEM_APPLYING">
          Apply is in progress. This page will update when processing finishes.
        </Alert>
      ) : null}

      {approveError ? (
        <Alert severity="error" className="mb-3" data-testid="SIMILAR_ITEM_APPROVE_ERROR">
          {approveError}
        </Alert>
      ) : null}

      {decisionError ? (
        <Alert severity="error" className="mb-3" data-testid="SIMILAR_ITEM_DECISION_ERROR">
          {decisionError}
        </Alert>
      ) : null}

      <Box className="flex flex-wrap items-center gap-2">
        <Button
          variant="contained"
          color="primary"
          disabled={applyActionsDisabled}
          onClick={() => setApproveConfirmOpen(true)}
          data-testid="SIMILAR_ITEM_APPROVE"
        >
          {approving || isApplying
            ? 'Applying…'
            : preview.status === 'approved'
              ? `Retry apply to ${preview.affected_count} similar item${preview.affected_count === 1 ? '' : 's'}`
              : `Apply to ${preview.affected_count} similar item${preview.affected_count === 1 ? '' : 's'}`}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          disabled={decisionActionsDisabled}
          onClick={onIgnore}
          data-testid="SIMILAR_ITEM_IGNORE"
        >
          {ignoring ? 'Dismissing…' : 'Ignore for now'}
        </Button>
        <Button
          variant="text"
          color="inherit"
          disabled={decisionActionsDisabled}
          onClick={() => setRejectConfirmOpen(true)}
          data-testid="SIMILAR_ITEM_REJECT"
        >
          {rejecting ? 'Rejecting…' : 'Reject'}
        </Button>
        {approving || rejecting || ignoring ? (
          <CircularProgress size={20} aria-label="Processing similar-item decision" />
        ) : null}
      </Box>

      <Dialog
        open={approveConfirmOpen}
        onClose={() => setApproveConfirmOpen(false)}
        aria-labelledby="similar-item-approve-dialog-title"
      >
        <DialogTitle id="similar-item-approve-dialog-title">
          Apply domain to similar items?
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" className="mb-2">
              Domain change: <strong>{preview.prior_domain}</strong> → <strong>{preview.target_domain}</strong>
            </Typography>
            <Typography variant="body2" className="mb-2">
              Items to update: <strong>{preview.affected_count}</strong>
            </Typography>
            <Typography variant="body2">{preview.override_impact.statement}</Typography>
            <Typography variant="body2" color="text.secondary" className="mt-2">
              {preview.undo_posture.statement}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmApprove}
            data-testid="SIMILAR_ITEM_APPROVE_CONFIRM"
          >
            Approve and apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectConfirmOpen}
        onClose={() => setRejectConfirmOpen(false)}
        aria-labelledby="similar-item-reject-dialog-title"
      >
        <DialogTitle id="similar-item-reject-dialog-title">
          Reject this suggestion?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            No items will be changed. You can still correct items individually.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={rejecting}
            onClick={handleConfirmReject}
            data-testid="SIMILAR_ITEM_REJECT_CONFIRM"
          >
            Reject suggestion
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SimilarItemSuggestionPreview;
